import { useState } from "react";
import {
  ArrowLeft,
  Loader2,
  Lock,
  Package,
  PencilLine,
  RotateCcw,
  Save,
} from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import { useAppDispatch } from "../../hooks/useStore";
import { reopenContainerOrder } from "../../store/slices/containerOrdersSlice";
import Button from "../UI/Button";
import { useConfirm } from "../../hooks/useConfirm";
import type { ContainerOrder } from "../../types/containerOrders";

interface ContainerOrderHeaderProps {
  order?: ContainerOrder;
  saving: boolean;
  loading: boolean;
  readonly: boolean;
  /** Cancelled orders are pinned to read mode; the toggle is withdrawn. */
  isCancelled: boolean;
  viewMode: "read" | "edit";
  canToggleMode: boolean;
  onToggleMode: () => void;
  onBack: () => void;
}

/**
 * The container order form's sticky header.
 *
 * Laid out as two rows that collapse into one when there is room, rather than a
 * single row that overflows when there isn't: identity (back, order number,
 * client) above, actions below. The order number is the one thing that
 * must never be clipped, so it is the only part allowed to consume the
 * leftover width.
 */
export function ContainerOrderHeader({
  order,
  saving,
  loading,
  readonly,
  isCancelled,
  viewMode,
  canToggleMode,
  onToggleMode,
  onBack,
}: ContainerOrderHeaderProps) {
  const { t } = useTranslation(["containerOrders", "common"]);
  const { confirm, confirmDialog } = useConfirm();
  const dispatch = useAppDispatch();
  const [statusBusy, setStatusBusy] = useState(false);

  const isReading = viewMode === "read";

  const runStatusChange = async (
    action: typeof reopenContainerOrder,
    confirmKey: string,
    successKey: string,
    dialog: { titleKey: string; actionKey: string; tone?: "warning" | "neutral" },
  ) => {
    if (!order) return;

    const agreed = await confirm({
      title: t(dialog.titleKey),
      // The existing sentence becomes the description: it already explains the
      // consequence, which is the part `window.confirm` had to cram into a
      // title it did not have.
      description: t(confirmKey),
      confirmLabel: t(dialog.actionKey),
      tone: dialog.tone ?? "warning",
    });
    if (!agreed) return;

    setStatusBusy(true);
    try {
      await dispatch(
        action({ id: order._id, updatedAt: order.updatedAt }),
      ).unwrap();
      toast.success(t(successKey));
    } catch (error: unknown) {
      // The rejection message is the server's explanation of *why* — a child
      // already in transit, an invoice already raised — so show it as-is.
      toast.error(
        typeof error === "string" ? error : t("toasts.statusChangeFailed"),
      );
    } finally {
      setStatusBusy(false);
    }
  };

  return (
    <div className="sticky top-0 z-20 mb-3 border-b border-[rgba(11,15,14,0.08)] bg-white/95 backdrop-blur supports-backdrop-filter:bg-white/80">
      {/*
        Two stacked regions on a narrow screen, one row once there is room.
        Stacking rather than wrapping is what keeps the order number readable:
        in a single wrapping row the four action buttons claim the width first
        and squeeze the title down to an ellipsis. Given its own line, the title
        gets the full width and the actions sit left-aligned beneath it.
      */}
      <div className="mx-auto flex w-full max-w-360 flex-col gap-2 px-3 py-2 sm:flex-row sm:items-center sm:gap-3 sm:px-4">
        {/* ── Identity ─────────────────────────────────────────────────── */}
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={onBack}
            className="shrink-0 p-1.5"
            aria-label={t("common:buttons.back", { defaultValue: "Back" })}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>

          <Package className="h-4 w-4 shrink-0 text-primary" />

          <div className="min-w-0">
            <h1 className="truncate text-[15px] font-bold leading-tight text-ink">
              {order?.orderNumber ?? t("title.new")}
            </h1>
            {order?.client?.companyName && (
              <p className="truncate text-[11px] leading-tight text-mist">
                {order.client.companyName}
              </p>
            )}
          </div>

          {/* Which mode the page is in, stated rather than inferred. Without
              this a read-only order and an editable one look alike until you
              try to type into one. */}
          {order && (
            <span
              className={`hidden shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide @sm:inline-flex sm:inline-flex ${
                isReading
                  ? "bg-secondary-100 text-secondary-700"
                  : "bg-primary-100 text-primary-800"
              }`}
            >
              {isReading ? (
                <Lock className="h-3 w-3" />
              ) : (
                <PencilLine className="h-3 w-3" />
              )}
              {isReading ? t("mode.viewing") : t("mode.editing")}
            </span>
          )}
        </div>

        {/* ── Actions ──────────────────────────────────────────────────── */}
        <div className="flex flex-wrap items-center gap-2 sm:justify-end">
          {order && isCancelled && (
            <Button
              type="button"
              variant="outline"
              disabled={statusBusy}
              onClick={() =>
                runStatusChange(
                  reopenContainerOrder,
                  "confirm.reopenOrder",
                  "toasts.reopened",
                  {
                    titleKey: "confirm.titles.reopenOrder",
                    actionKey: "confirm.actions.reopenOrder",
                    // Reopening restores rather than destroys — it is a
                    // question, not a warning.
                    tone: "neutral",
                  },
                )
              }
            >
              <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
              {t("actions.reopenOrder")}
            </Button>
          )}

          {/* The mode switch. In read mode it is the page's primary action —
              it is what you press to do anything at all — so it gets the
              primary treatment and Save is not rendered at all. */}
          {canToggleMode && isReading && (
            <Button
              type="button"
              variant={isReading ? "primary" : "outline"}
              onClick={onToggleMode}
              disabled={saving}
            >
                <PencilLine className="mr-1.5 h-3.5 w-3.5" />
              {t("actions.editOrder")}
            </Button>
          )}

          {/* Hidden in read mode rather than disabled: there is nothing to
              save, and a permanently greyed-out primary button reads as
              something being broken. */}
          {!readonly && (
            <Button type="submit" disabled={saving || loading}>
              {saving ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Save className="mr-1.5 h-3.5 w-3.5" />
              )}
              {t("actions.saveOrder")}
            </Button>
          )}
        </div>
      </div>

      {isCancelled && (
        <div className="border-t border-[rgba(11,15,14,0.06)] bg-secondary-50">
          <p className="mx-auto w-full max-w-360 px-3 py-1.5 text-[11px] text-secondary-600 sm:px-4">
            {t("hints.cancelledReadonly")}
          </p>
        </div>
      )}
      {confirmDialog}
    </div>
  );
}
